import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  IconButton,
  Rating,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Visibility as EyeIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import apiClient from '../config/axios';
import dayjs from 'dayjs';
import getConfig from '../config/app-config';
import { useAuth } from '../contexts/AuthContext';
import WallLogbookStats from '../components/WallLogbookStats';
import WallLogbookGradeChart from '../components/WallLogbookGradeChart';

const getWallColorHex = (color) => {
  const wallColorMap = {
    giallo: '#FBC02D',
    gialla: '#FBC02D',
    nero: '#212121',
    nera: '#212121',
    verde: '#388E3C',
    rossa: '#D32F2F',
    blu: '#1976D2',
    bianco: '#FAFAFA',
    bianca: '#FAFAFA',
    arancione: '#F57C00',
    viola: '#8E24AA',
  };
  return wallColorMap[color] || '#9E9E9E';
};

const getWallStatusLabel = (row) => (
  row.status === 'active' && row.wallId ? 'Presente' : 'Rimossa'
);

const WallLogbook = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const config = getConfig();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [myAscents, setMyAscents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [ascentsRes, statsRes] = await Promise.all([
        apiClient.get('/api/wall-ascents/me'),
        apiClient.get('/api/wall-ascents/me/stats'),
      ]);
      setMyAscents(ascentsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Errore caricamento diario:', err);
      setError('Errore nel caricamento del diario. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  const allColumns = useMemo(() => [
    {
      field: 'climbedAt',
      headerName: 'Data',
      flex: 0.8,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2">
            {dayjs(params.value).format('DD-MMM-YY')}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'wall',
      headerName: 'Via',
      flex: 0.7,
      sortable: false,
      valueGetter: (_, row) => {
        const snap = row.wallSnapshot;
        return `${snap?.wallNumber ?? '-'} · ${snap?.wallColor ?? '-'}`;
      },
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Stato',
      flex: 0.6,
      valueGetter: (_, row) => getWallStatusLabel(row),
      renderCell: (params) => {
        const isPresente = params.value === 'Presente';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <Typography
              variant="body2"
              sx={{
                color: isPresente ? 'success.main' : 'error.main',
                fontWeight: 500,
              }}
            >
              {params.value}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'wallColor',
      headerName: 'Colore',
      flex: 0.4,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Tooltip title={params.row.wallSnapshot?.wallColor || '-'}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: getWallColorHex(params.row.wallSnapshot?.wallColor),
                border: params.row.wallSnapshot?.wallColor === 'bianco' ? '1px solid #BDBDBD' : 'none',
              }}
            />
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'officialGrade',
      headerName: isMobile ? 'Grado -\nGrado Proposto' : 'Grado',
      flex: 0.7,
      sortable: false,
      valueGetter: (_, row) => row.wallSnapshot?.officialGrade,
      renderCell: (params) => {
        const showProposed = isMobile && params.row.grade;
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              alignItems: 'flex-start',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Chip
              label={params.value || '-'}
              color="primary"
              variant="contained"
              size="small"
            />
            {showProposed && (
              <Chip
                label={params.row.grade}
                color="secondary"
                variant="contained"
                size="small"
              />
            )}
          </Box>
        );
      },
    },
    {
      field: 'grade',
      headerName: 'Proposto',
      flex: 0.7,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
          <Chip
            label={params.value || '-'}
            color="secondary"
            variant="contained"
            size="small"
          />
        </Box>
      ),
    },
    {
      field: 'rating',
      headerName: 'Bellezza',
      flex: isMobile ? 1 : 0.6,
      renderCell: (params) => {
        const statusLabel = getWallStatusLabel(params.row);
        const isPresente = statusLabel === 'Presente';
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              alignItems: 'flex-start',
              justifyContent: 'center',
              height: '100%',
              width: '100%',
            }}
          >
            <Rating value={params.value || 0} readOnly precision={0.5} size="small" />
            {isMobile && (
              <Typography
                variant="caption"
                sx={{
                  color: isPresente ? 'success.main' : 'error.main',
                  fontWeight: 500,
                }}
              >
                {statusLabel}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.35,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const isActive = params.row.status === 'active' && params.row.wallId;
        if (!isActive) return null;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
            <Tooltip title="Visualizza dettagli via">
              <IconButton
                onClick={() => navigate(`/walls/${params.row.wallId}`)}
                color="primary"
                size="small"
              >
                <EyeIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ], [navigate, isMobile]);

  const columns = useMemo(() => {
    if (isMobile) {
      return allColumns.filter((col) => !['wall', 'wallColor', 'status', 'grade'].includes(col.field));
    }
    return allColumns;
  }, [isMobile, allColumns]);

  const rowHeight = isMobile ? 88 : 56;
  const headerHeight = isMobile ? 72 : 56;

  if (!config.WITH_WALLS) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Questa funzionalità è disponibile solo per palestre di arrampicata.</Alert>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="info">Accedi per vedere il tuo diario arrampicata.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Diario Arrampicata
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Le tue ripetizioni restano qui anche se una via viene rimossa dalla palestra. Solo tu puoi vederle.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <WallLogbookStats stats={stats} />
      <WallLogbookGradeChart gradeDistribution={stats?.gradeDistribution} />

      {myAscents.length === 0 ? (
        <Alert severity="info">
          Nessuna ripetizione registrata. Aggiungi un grado proposto da una via in &quot;Vie&quot;.
        </Alert>
      ) : (
        <Paper sx={{ width: '100%' }}>
          <DataGrid
            rows={myAscents}
            columns={columns}
            getRowId={(row) => row._id}
            rowHeight={rowHeight}
            columnHeaderHeight={headerHeight}
            disableRowSelectionOnClick
            disableColumnResize
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={{
              // height: 420,
              border: 0,
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'background.paper',
              },
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: 'background.paper',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'pre-line',
                lineHeight: 1.2,
              },
              '& .MuiDataGrid-virtualScroller': {
                overflowY: 'scroll',
                scrollbarGutter: 'stable',
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
                overflow: 'hidden',
              },
            }}
          />
        </Paper>
      )}
    </Container>
  );
};

export default WallLogbook;
